import moment from 'moment';
import { FilterSetupCalendarType } from '../services/filterService';

export interface TuhonoFilterDateRange {
  startDate: string;
  endDate: string;
}

export interface TuhonoCalendarSelection {
  calendarType: FilterSetupCalendarType;
  calendarTypeYear: number;
  calendarTypeMonth: number;
  calendarTypeWeek: number;
  calendarTypeDay: number;
}

/**
 * The React Tūhono calendar route. Search and counts are new behaviour for
 * this route only; the legacy Angular route keeps its original filters.
 */
export const isReactTuhonoPath = (): boolean => {
  if (typeof window === 'undefined') return false;

  return window.location.pathname.replace(/\/+$/, '') === '/maramatakacalendar';
};

/**
 * Mirrors the calendar component's loaded period so filter counts describe the
 * same data the calendar is showing. The calendar deliberately keeps a buffer
 * around the selected period so the wheel can be rotated; counts use the same
 * buffer rather than only the selected slice.
 *
 * - day: selected day ± 2 weeks
 * - week: selected week ± 12 weeks
 * - month: selected month ± 1 year
 */
export const getTuhonoFilterDateRange = (selection: TuhonoCalendarSelection): TuhonoFilterDateRange => {
  const now = moment();
  const year = Number.isFinite(selection.calendarTypeYear) ? selection.calendarTypeYear : now.year();
  const month =
    Number.isInteger(selection.calendarTypeMonth) &&
    selection.calendarTypeMonth >= 0 &&
    selection.calendarTypeMonth <= 11
      ? selection.calendarTypeMonth
      : now.month();

  const monthStart = now.year(year).month(month).date(1).startOf('day');

  if (selection.calendarType === 'week') {
    const week = selection.calendarTypeWeek > 0 ? selection.calendarTypeWeek : 1;
    const weekStart = monthStart.clone().add((week - 1) * 7, 'days');

    return {
      startDate: weekStart.clone().subtract(12, 'weeks').format('YYYY-MM-DD'),
      endDate: weekStart.clone().add(12, 'weeks').endOf('week').format('YYYY-MM-DD'),
    };
  }

  if (selection.calendarType === 'month') {
    return {
      startDate: monthStart.clone().subtract(1, 'years').date(1).format('YYYY-MM-DD'),
      endDate: monthStart.clone().add(1, 'years').date(31).format('YYYY-MM-DD'),
    };
  }

  const requestedDay = selection.calendarTypeDay > 0 ? selection.calendarTypeDay : 1;
  const selectedDay = monthStart.clone().date(requestedDay);

  return {
    startDate: selectedDay.clone().subtract(2, 'weeks').format('YYYY-MM-DD'),
    endDate: selectedDay.clone().add(2, 'weeks').format('YYYY-MM-DD'),
  };
};

/**
 * Case-insensitive free-text match used by Tūhono's shared search.
 */
export const matchesTuhonoSearch = (value: unknown, searchTerm: string): boolean => {
  const term = (searchTerm || '').trim().toLowerCase();
  if (!term) return true;

  return `${value ?? ''}`.toLowerCase().includes(term);
};

const MATAURANGA_SEARCH_FIELDS = [
  'name',
  'title_reo_maori',
  'title_english',
  'event_type_reo_maori',
  'event_type_english',
  'about_reo_maori',
  'about_english',
  'description_reo_maori',
  'description_english',
  'source_name',
];

export const mataurangaMatchesSearch = (matauranga: any, searchTerm: string): boolean => {
  if (!searchTerm || !searchTerm.trim()) return true;

  return MATAURANGA_SEARCH_FIELDS.some((field) => matchesTuhonoSearch(matauranga?.[field], searchTerm));
};

const ENVIRONMENTAL_DATA_SOURCE_SEARCH_FIELDS = ['human_readable_name', 'name', 'source_identifier'];

export const environmentalDataSourceMatchesSearch = (source: any, searchTerm: string): boolean => {
  if (!searchTerm || !searchTerm.trim()) return true;

  return ENVIRONMENTAL_DATA_SOURCE_SEARCH_FIELDS.some((field) => matchesTuhonoSearch(source?.[field], searchTerm));
};
